import React from "react";
import { Modal, Button, message } from "antd";
import { observer } from "mobx-react";
import classNames from "classnames";
import citationBulkUploadStore from "@app/state/store/citation/bulk-upload";
import "../bulk-upload/style/results-modal.scoped.scss";

const CitationBulkUploadResultsModal = observer(() => {
    const { resultModalOpen, uploads } = citationBulkUploadStore;

    const results = uploads
        .map((upload) => {
            let description = "";
            let status = "";
            let isError = false;
            const { citations = 0, skipped = 0 } = upload;

            switch (upload.status) {
                case "success":
                    if (upload.message) {
                        description = upload.message;
                    } else if (citations > 0) {
                        description = `${citations} ${
                            citations === 1 ? "citation" : "citations"
                        } created successfully${skipped > 0 ? `, ${skipped} skipped` : ""}`;
                    } else {
                        description = "Upload completed successfully";
                    }
                    status = "Success";
                    isError = false;
                    break;
                case "error":
                    description = upload.message || upload.error || "Upload failed";
                    status = "Error";
                    isError = true;
                    break;
                case "queued":
                    description = "Job queued for processing";
                    status = "Queued";
                    isError = false;
                    break;
                case "processing":
                    description = `Processing... ${upload.progress || 0}%`;
                    status = "Processing";
                    isError = false;
                    break;
                case "skipped":
                    description = upload.message || upload.error || "Skipped";
                    status = "Skipped";
                    isError = true;
                    break;
                default:
                    description = upload.message || "Unknown status";
                    status = "Unknown";
                    isError = false;
                    break;
            }

            return {
                name: upload.name,
                status,
                description,
                isError,
            };
        })
        .sort((a, b) => {
            const statusOrder = { error: 0, queued: 2, processing: 3, success: 4, skipped: 5 };
            return statusOrder[a.status] - statusOrder[b.status];
        });

    const handleOk = async () => {
        const csvHeader = "File Name,Status,Description\n";
        const csvContent = results
            .map((result) => `"${result.name}","${result.status}","${result.description}"`)
            .join("\n");
        const csvString = csvHeader + csvContent;
        try {
            await navigator.clipboard.writeText(csvString);
            message.success("Results copied to clipboard");
        } catch (err) {
            message.error("Failed to copy results");
        }
    };

    const handleCancel = () => {
        citationBulkUploadStore.toggleResultsModal(false);
    };

    return (
        <Modal
            title="Bulk Upload Citations Result"
            visible={resultModalOpen}
            onOk={handleOk}
            onCancel={handleCancel}
            destroyOnClose={false}
            width="80%"
            footer={[
                <Button key="back" onClick={handleCancel}>
                    Close
                </Button>,
                <Button key="submit" type="primary" onClick={handleOk}>
                    Copy to clipboard
                </Button>,
            ]}
        >
            <table style={{ width: "100%" }}>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>File Name</th>
                        <th>Status</th>
                        <th>Description</th>
                    </tr>
                </thead>
                <tbody>
                    {results.map((result, index) => (
                        <tr
                            key={result.name}
                            className={classNames({
                                success: result.status === "Success" && !result.isError,
                                error: result.status === "Error" || result.isError,
                                warning: result.status === "Skipped",
                            })}
                        >
                            <td>{index + 1}</td>
                            <td>{result.name}</td>
                            <td>{result.status}</td>
                            <td>{result.description}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Modal>
    );
});

export default CitationBulkUploadResultsModal;
